import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useAuth } from '@/services/auth';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { Box } from '@customerio/pluma-components/react';
import { peopleFilterBuilder, type PeopleFilterBuilderMeta } from './builder';
import { ClearConditionsLink, ConditionGroup, type TypeOptionGroup } from '@/features/dryad';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { ATTRIBUTE_CONDITION_TYPE, isAttributeLocked } from './attribute-condition';
import { AttributeRow } from './attribute-row';
import { DeviceAttributeRow } from './device-attribute-row';
import { EventRow } from './event-row';
import { MessageRow } from './message-row';
import { NewsletterRecipientsRow } from './newsletter-recipients-row';
import { OptOutRow } from './opt-out-row';
import { SegAttrRow } from './seg-attr-row';
import { SegmentsRow } from './segments-row';
import { CreateSegmentForm } from './create-segment-form';
import { appendCreatedSegmentCondition } from './segments-plugin';
import { defaultOptOutChannel, hasAnyOptOutChannel } from '@/features/dryad';
import {
  ConditionType,
  PEOPLE_SEARCH_TYPE_LABELS,
  peopleSearchConditionTypeOf,
  type PeopleSearchConditionType,
} from './condition-types';
import { PEOPLE_FILTER_FORMAT } from './people-filter-format';
import { normalizePeopleFilterWire } from './people-filter-format';
import { decodePeopleFiltersParam } from './filters-param';
import type { JsonObject, JsonValue } from './wire-types';

peopleFilterBuilder.registerView(ATTRIBUTE_CONDITION_TYPE, AttributeRow);
peopleFilterBuilder.registerView(ConditionType.DeviceAttribute, DeviceAttributeRow);
peopleFilterBuilder.registerView(ConditionType.Event, EventRow);
peopleFilterBuilder.registerView(ConditionType.Message, MessageRow);
peopleFilterBuilder.registerView(ConditionType.NewsletterRecipients, NewsletterRecipientsRow);
peopleFilterBuilder.registerView(ConditionType.OptOut, OptOutRow);
peopleFilterBuilder.registerView(ConditionType.SegAttr, SegAttrRow);
peopleFilterBuilder.registerView(ConditionType.SegmentsNegatable, SegmentsRow);

/**
 * The condition types with a ported row. A type outside this set never
 * enters the add menu.
 */
const AVAILABLE_ROW_TYPES: PeopleSearchConditionType[] = [
  ATTRIBUTE_CONDITION_TYPE,
  ConditionType.DeviceAttribute,
  ConditionType.Event,
  ConditionType.Message,
  ConditionType.NewsletterRecipients,
  ConditionType.OptOut,
  ConditionType.SegmentsNegatable,
];

// Module-level so the nested builder isn't re-derived (and reset) each render.
const EMPTY_CONDITIONS: JsonObject = { and: [] };

export type PeopleFilterBuilderProps = {
  /** The encoded `filters` search param. */
  filters: string | null;
  onChange: (serialized: JsonValue, isValid: boolean, isEmpty: boolean) => void;
};

export type PeopleFilterConditionsProps = {
  /**
   * The tree the builder starts from. Replacing it re-derives the builder, so
   * callers keep it referentially stable across renders they don't intend as a
   * reset.
   */
  initialConditions: JsonValue;
  onChange: (serialized: JsonValue, isValid: boolean, isEmpty: boolean) => void;
  /** Suppresses the create-segment shortcut on nested surfaces. */
  withCreateSegmentShortcut?: boolean;
};

/**
 * The interactive people-search condition builder. Edits commit live: every
 * builder change surfaces through onChange with the serialized wire and its
 * validity, and the page owns the debounce, the commit gate, and the URL
 * write.
 */
export function PeopleFilterBuilder({ filters, onChange }: PeopleFilterBuilderProps) {
  const normalizedConditions = useMemo(() => normalizePeopleFilterWire(decodePeopleFiltersParam(filters)), [filters]);
  return (
    <PeopleFilterConditions initialConditions={normalizedConditions} onChange={onChange} withCreateSegmentShortcut />
  );
}

/**
 * One condition-builder surface: its own builder, provider, and group chrome.
 * The page-level filter uses it with the tree decoded from the URL param; the
 * inline create-segment form nests a second, independently-seeded instance for
 * the new segment's own definition.
 */
export function PeopleFilterConditions({
  initialConditions,
  onChange,
  withCreateSegmentShortcut = false,
}: PeopleFilterConditionsProps) {
  const { hasPermission } = usePermissions();
  const canViewAttributes = hasPermission('journeys/attribute_data/view');
  const { account } = useAuth();
  // The inline creator only builds data-driven segments, which Builder-plan
  // workspaces don't have, so the plan gates the shortcut ahead of permission.
  const canCreateSegments = !isBuilderPlan(account) && hasPermission('journeys/segments/create');
  const environment = useEnvironmentOptional();
  const taxonomy = useProductTaxonomy();

  const builder = peopleFilterBuilder.useConditionBuilder({
    initialValue: initialConditions,
    format: PEOPLE_FILTER_FORMAT,
    // A fresh opt-out leaf defaults to the first channel with a configured
    // sender, which the plugin's own default (SMS) can't know. Thunk form so
    // the identities loading after the builder mounts are still picked up;
    // parsed leaves are never seeded.
    seeds: {
      [ConditionType.OptOut]: () => ({ channel: defaultOptOutChannel(environment.data?.identities ?? []) }),
    },
  });

  // Subscribed read: the attribute lock and the group chrome must follow
  // every tree change, including the fresh root Clear Filters mints.
  const tree = useSyncExternalStore(builder.sources.tree.subscribe, builder.sources.tree.get);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // The live-commit bridge: every builder change (row edits, add/remove,
  // clear, and the one notification an external filters change produces)
  // reaches the page, which gates and debounces the URL write.
  useEffect(() => {
    const output = builder.sources.output(PEOPLE_FILTER_FORMAT);
    return output.subscribe(() => {
      const { serialized, isValid, isEmpty } = output.get();
      onChangeRef.current(serialized, isValid, isEmpty);
    });
  }, [builder]);

  // The phone-number row picks one of the workspace's SMS or WhatsApp senders,
  // so it is offered only where one is configured.
  const optOutAvailable = hasAnyOptOutChannel(environment.data?.identities ?? []);

  // The one label that follows the Newsletters → One-time sends rename.
  const newsletterTypeLabel = taxonomy.term('broadcasts.titleSingular');

  const typeOptions: TypeOptionGroup[] = useMemo(() => {
    const gatedTypes = AVAILABLE_ROW_TYPES.filter((type) => {
      if (type === ATTRIBUTE_CONDITION_TYPE) {
        return canViewAttributes;
      }
      if (type === ConditionType.OptOut) {
        return optOutAvailable;
      }
      return true;
    });
    return [
      {
        label: 'People',
        options: gatedTypes.map((type) => ({
          value: type,
          label:
            type === ConditionType.NewsletterRecipients
              ? newsletterTypeLabel
              : (PEOPLE_SEARCH_TYPE_LABELS.get(type) ?? type),
        })),
      },
    ];
  }, [canViewAttributes, optOutAvailable, newsletterTypeLabel]);

  // Which segment row opened the create form, so the created segment can fill
  // that row in place when it's still empty. Null means the form is closed.
  const [creatingFromNodeId, setCreatingFromNodeId] = useState<string | null>(null);

  // Only offered where the shortcut is enabled and the viewer may create
  // segments; the row hides its picker entry when this is undefined. The
  // state setter is passed as-is: the row memoizes its picker entry on this
  // identity, and the builder re-renders on every tree change.
  const onCreateSegment = withCreateSegmentShortcut && canCreateSegments ? setCreatingFromNodeId : undefined;

  const handleSegmentCreated = (segmentId: number) => {
    appendCreatedSegmentCondition(builder, segmentId, creatingFromNodeId ?? undefined);
    setCreatingFromNodeId(null);
  };

  const meta: PeopleFilterBuilderMeta = {
    isDisabled: isAttributeLocked(tree, { canViewAttributes, disabled: false }),
    typeOptions,
    canViewAttributeValues: canViewAttributes,
    onCreateSegment,
  };

  return (
    <Box data-testid="people-filter-builder">
      <peopleFilterBuilder.DryadProvider builder={builder} meta={meta}>
        <ConditionGroup conditionTypeOf={peopleSearchConditionTypeOf} headerActions={<ClearConditionsLink />} />
      </peopleFilterBuilder.DryadProvider>
      {creatingFromNodeId != null ? (
        <CreateSegmentForm
          onCreated={handleSegmentCreated}
          onCancel={() => setCreatingFromNodeId(null)}
          // The new segment's own tree: a second builder instance, seeded
          // empty. Its provider is nested, so its rows read this inner tree
          // while the outer rows keep reading the filter's. The shortcut isn't
          // offered again inside it.
          renderConditions={(onConditionsChange) => (
            <PeopleFilterConditions initialConditions={EMPTY_CONDITIONS} onChange={onConditionsChange} />
          )}
        />
      ) : null}
    </Box>
  );
}
